import { Link } from 'react-router-dom';
import { useParallax } from '@/hooks/useScrollAnimation';

export default function HeroSection() {
  const blob = useParallax(0.12);
  const imgParallax = useParallax(0.08);
  const cardParallax = useParallax(-0.06);

  return (
    <section className="relative overflow-hidden bg-background-100">
      {/* Texture + decorative layers */}
      <div className="absolute inset-0 texture-grid opacity-60" />
      <div
        ref={blob.ref}
        className="pointer-events-none absolute -top-24 -right-24 w-[34rem] h-[34rem] rounded-full bg-primary-100/60 blur-3xl"
        style={{ transform: `translateY(${blob.offset}px)` }}
      />
      <div className="pointer-events-none absolute bottom-0 -left-32 w-96 h-96 rounded-full bg-accent-200/50 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 w-24 h-24 border-2 border-dashed border-accent-400/40 rounded-full animate-spin-slow" />

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-12 md:pt-16 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Left: Text — 6 cols */}
            <div className="lg:col-span-6 order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50 text-primary-600 text-xs font-bold uppercase tracking-[0.15em] mb-6 border border-primary-200 reveal is-in">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
                Clínica Tarija Vitalmedic SRL
              </span>
              <h1 className="font-heading text-[2.6rem] md:text-6xl lg:text-[4.2rem] font-medium text-foreground-950 leading-[1.02] mb-6 text-balance">
                Tu salud, cuidada con{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 italic text-primary-600">calidez</span>
                  <span className="absolute bottom-1 left-0 right-0 h-3 md:h-4 bg-accent-400/60 -z-0" />
                </span>{' '}
                humana
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
                Atención médica integral con más de 15 especialidades y emergencias las 24 horas, en el corazón de Tarija. Un equipo que te trata como familia.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-10">
                <Link
                  to="/contacto"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-check-line" />
                  </span>
                  Solicitar Turno
                  <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
                <Link
                  to="/servicios"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-background-50 border border-background-300 text-foreground-800 text-sm font-semibold hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-stethoscope-line" />
                  </span>
                  Ver especialidades
                </Link>
              </div>

              {/* Trust row */}
              <div className="flex items-center gap-5">
                <div className="flex -space-x-3">
                  {[
                    'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/f53a02f12d709b868683438b16a57827.png',
                    'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/751a4ba43261092a8c20ee9dd233057a.png',
                    'https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/5ffc415b0c6b5998bcc18c756456648b.png',
                  ].map((src, i) => (
                    <div key={i} className="w-11 h-11 rounded-full border-2 border-background-50 overflow-hidden bg-background-200">
                      <img src={src} alt="Equipo Vitalmedic" className="w-full h-full object-cover object-top" />
                    </div>
                  ))}
                  <div className="w-11 h-11 rounded-full border-2 border-background-50 bg-accent-500 flex items-center justify-center text-secondary-950 text-xs font-bold">
                    +5k
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-accent-500">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <i key={i} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                  <p className="text-xs text-foreground-600 mt-0.5">+5000 pacientes atendidos</p>
                </div>
              </div>
            </div>

            {/* Right: Layered image — 6 cols */}
            <div className="lg:col-span-6 order-1 lg:order-2 relative">
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                {/* Accent frame behind */}
                <div className="absolute -top-4 -right-4 bottom-8 left-10 rounded-[2.5rem] bg-accent-400/30 -z-10" />

                {/* Main organic image */}
                <div
                  ref={imgParallax.ref}
                  className="relative rounded-[2.5rem] overflow-hidden border-4 border-background-50"
                  style={{ transform: `translateY(${imgParallax.offset}px)` }}
                >
                  <img
                    src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/751a4ba43261092a8c20ee9dd233057a.png"
                    alt="Plantel médico y de enfermería de Clínica Tarija Vitalmedic SRL"
                    className="w-full h-[380px] md:h-[520px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/30 via-transparent to-transparent" />
                </div>

                {/* Floating card — emergencias */}
                <div
                  ref={cardParallax.ref}
                  className="absolute -bottom-5 -left-3 md:-left-8 bg-background-50 rounded-2xl p-4 border border-background-200 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)]"
                  style={{ transform: `translateY(${cardParallax.offset}px)` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500 text-background-50">
                      <i className="ri-24-hours-line text-2xl" />
                    </div>
                    <div>
                      <p className="font-heading text-xl font-bold text-foreground-950 leading-none">24/7</p>
                      <p className="text-xs text-foreground-600 mt-1">Emergencias abiertas</p>
                    </div>
                  </div>
                </div>

                {/* Floating badge — specialties */}
                <div className="absolute top-6 -right-2 md:-right-6 bg-secondary-950 text-background-50 rounded-2xl px-4 py-3 animate-float">
                  <p className="font-heading text-2xl font-bold text-accent-500 leading-none">+15</p>
                  <p className="text-[11px] text-background-200 mt-1">Especialidades</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scrolling keyword marquee */}
      <div className="relative border-y border-background-200 bg-background-50 py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {['Pediatría', 'Ginecología', 'Cardiología', 'Neurología', 'Traumatología', 'Cirugía General', 'Oftalmología', 'Nefrología'].map((kw) => (
                <span key={kw + dup} className="flex items-center gap-3 px-6 text-sm font-medium text-foreground-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                  {kw}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}